import countryCodes from "../lib/countryCodes";
import { isEmail, isTelephone } from "../lib/formsFunctions";
import { errorTypes } from "../types";

interface IGetErrorValue {
  value: string;
  required?: boolean;
  format?: string;
  dateRange?: { from: string; to: string };
  currentLanguage?: string;
  options: any;
  noSpaceAround?: boolean;
  minLength?: number;
  bookingFormField: {
    name: string;
    options?: any[];
  };
  bookingFormFields?: Record<string, any>;
}

export const GetErrorValue = ({
  value,
  required,
  format,
  dateRange,
  currentLanguage,
  options,
  noSpaceAround,
  minLength,
  bookingFormField,
  bookingFormFields = {},
}: IGetErrorValue): string => {
  // languages that are not validated by ascii validations
  const restrictedLanguages = ["sk", "cs"];

  if (required && !value) {
    return errorTypes.REQUIRED;
  }

  if (required && value && value.length < minLength) {
    return errorTypes.MIN_LENGTH;
  }

  if (bookingFormField.name.includes("SeatOption")) {
    if (
      validatePassengerDuplicitySeats(
        bookingFormFields,
        bookingFormField.name
      ) === false
    ) {
      return errorTypes.SEAT_DUPLICATE;
    }
  }

  if (
    format === "email" &&
    !isEmail(value) &&
    !(value === "@" && !required) &&
    !(value === "" && !required)
  ) {
    return errorTypes.EMAIL;
  }

  if (value && noSpaceAround && value !== value.trim()) {
    return errorTypes.NO_SPACE_AROUND;
  }

  if (["number", "country_calling_code"].includes(format)) {
    if (value?.length === 0 && !required) {
      return "";
    }

    // if there is the prefix only - already preffiled
    if (countryCodes.find((countryCode) => countryCode.code === value)) {
      return "";
    }

    if (
      (isTelephone(value) &&
        !(!value && !required) &&
        !countryCodes.find((countryCode) => countryCode.code === value)) ===
      false
    ) {
      return errorTypes.TELEPHONE;
    }

    if (format === "country_calling_code" && value && !value.startsWith("+")) {
      return errorTypes.TELEPHONE;
    }
  }

  if (format === "date" && !isCorrectDate(value, dateRange, required)) {
    return errorTypes.DATE;
  }

  if (
    format === "ascii_alphabet" &&
    !restrictedLanguages.includes(currentLanguage) &&
    !isAsciiAlphabetCharset(value)
  ) {
    return errorTypes.UNSUPPORTED_ASCII_ALPHABET_CHARSET;
  }

  if (format === "alphanumeric" && !isAlphanumericCharset(value)) {
    return errorTypes.UNSUPPORTED_ALPHANUMERIC_CHARSET;
  }

  if (
    format === "ascii_text" &&
    !restrictedLanguages.includes(currentLanguage) &&
    !asciiCharset(value)
  ) {
    return errorTypes.UNSUPPORTED_ASCII_TEXT_CHARSET;
  }

  if (format === "postcode" && !isPostcodeCharset(value)) {
    return errorTypes.UNSUPPORTED_POSTCODE_CHARSET;
  }

  // wrongly prefilled passenger title cached TF -> GWS different case
  if (
    options?.length > 0 &&
    value &&
    !options.find((option) => option.value === value) &&
    required
  ) {
    return "REQUIRED";
  }

  return "";
};

const isPostcodeCharset = (val) => new RegExp("^[a-zA-Z0-9 ]{0,12}$").test(val);

const isAsciiAlphabetCharset = (val) => new RegExp("^[a-zA-Z ]*$").test(val);

export const isAlphanumericCharset = (val) =>
  new RegExp("^[a-zA-Z0-9]*$").test(val);

export function validatePassengerDuplicitySeats(
  bookingFormFields: Record<string, any>,
  seatFieldName: string
): boolean {
  const match = seatFieldName.match(/^(\d+)-SeatOptions_(\d+)$/);
  if (!match) {
    return true;
  }
  const currentPassengerIndex = match[1];
  const currentFlightId = match[2];
  const currentSeatValue = bookingFormFields[seatFieldName]?.value;

  if (!currentSeatValue || currentSeatValue === "") {
    return true;
  }

  const hasDuplicate = Object.keys(bookingFormFields).some((fieldKey) => {
    if (fieldKey === seatFieldName) {
      return false;
    }
    const otherMatch = fieldKey.match(/^(\d+)-SeatOptions_(\d+)$/);
    if (otherMatch) {
      const otherPassengerIndex = otherMatch[1];
      const otherFlightId = otherMatch[2];
      const otherSeatValue = bookingFormFields[fieldKey]?.value;
      if (
        otherFlightId === currentFlightId &&
        otherSeatValue === currentSeatValue &&
        otherSeatValue !== "" &&
        otherPassengerIndex !== currentPassengerIndex
      ) {
        return true;
      }
    }
    return false;
  });
  return !hasDuplicate;
}

const asciiCharset = (val) =>
  new RegExp("^[#?.,;:'°&/()\\- a-zA-Z0-9]*$").test(val);

const isCorrectDate = (value, dateRange, required) => {
  if (!required && !value) {
    return true;
  }

  if (dateRange && new Date(value) < new Date(dateRange.from)) {
    return false;
  }
  if (dateRange && new Date(value) > new Date(dateRange.to)) {
    return false;
  }
  return (
    value.split("-").filter((oldValue) => Number(oldValue) > 0).length === 3
  );
};
